// The redesigned page's content (?page=redesign, js/redesign/redesign.js),
// as data shaped like a Shopify template: the sections in page order, each
// with its `type` (the section file it would be: one module and one
// stylesheet here, js/redesign/sections/ and css/redesign/), its `settings`
// and its `blocks`. This is the standalone site's content and the theme's
// defaults: npm run shopify (scripts/shopify-package.mjs) turns it into the
// section's settings and blocks (docs/shopify.md), where the store's owner
// edits it; in the theme the page reads the section's, in this same shape
// (createRedesign's `sections`).
//
// Copy is the store's, verbatim: the owner's pared-down product page
// (nothingradio.com/products/nothing-radio-usb, 2026-10-20), its words, in
// its order, in this page's design; never write new copy here.
// The drive's photos (photos/manifest.json) are blocks { type: "photo",
// folder, name }; of the drive's photoshoot of a person only the photos she
// agreed to show may appear (PRIVATE_ALLOWED; driveAllowed, which the page
// checks every drive photo against: tests/redesign.test.mjs). (In the theme
// a photo on white can instead be one of the store's images, { src, width,
// height, file }: the section's image picker; the stack's and the gallery's
// drive photos are named in text areas: drivePhotoLines.)
// The store's own videos and photos (the AliRQ video's, the gallery's) are
// its public Shopify CDN URLs (not committed here), each video its largest
// mp4 up to 1080p, as the theme picks it.

const CDN = "https://nothingradio.com/cdn/shop";
// a video on the store's CDN, by its Shopify video id and rendition, with
// its preview image `poster` px wide
const clip = (id, rendition, poster = 1200) => ({
  src: `${CDN}/videos/c/vp/${id}/${id}.${rendition}.mp4?v=0`,
  poster: `${CDN}/files/preview_images/${id}.thumbnail.0000000000_${poster}x.jpg`,
});
// a photo on the store's CDN, by its file there, its size and the name its
// window's bar says
const photo = (cdnFile, width, height, file) => ({ type: "photo", src: `${CDN}/files/${cdnFile}`, width, height, file, caption: null });
// the store's videos are phone videos, upright, or wide (16:9)
const UPRIGHT = 9 / 16;
const WIDE = 16 / 9;

// a photo on the drive, by its folder and the id its file name ends with
// (the drive's names are "<date>-<photographer>-" twice, then the camera's)
const drive = (folder, prefix) => (id) => ({ type: "photo", folder, name: `${prefix}${id}.jpg` });
const product = drive("product", "20260603-CarterHouck-20260603-CarterHouck-");
const scanner = drive("scanner", "20260613-CarterHouck-20260613-CarterHouck-");
const shoot = (id) => drive(PRIVATE_FOLDER, id.startsWith("P10") ? "20260915-CarterHouck-20000101-CarterHouck-" : "20260915-CarterHouck-20260915-CarterHouck-")(id);

/** The drive's photoshoot of a person: private but for the photos she
 * agreed to show and the store's owner picked (Kyle, 2026-10-20), by the id
 * their names end with. No other photo of that folder may appear on the
 * page (driveAllowed), nor leave the repository: the theme's drive has
 * these and no other of the folder (scripts/shopify-photos.mjs). The one
 * list of them, for the page, the theme's photos and the editor's check
 * alike. The folder's name, on the site's drive and the theme's, names no
 * one (scripts/photos.config.json's sources keeps its source folder's). */
export const PRIVATE_FOLDER = "photoshoot";
export const PRIVATE_ALLOWED = Object.freeze([
  "DSC08449", "DSC08482", "DSC08485", "DSC08487", "DSC08499", "DSC08500", "DSC08509", "DSC08513", "DSC08514", "DSC08529", "DSC08534", "DSC08535",
  "DSC08544", "DSC08632", "DSC08634", "DSC08695", "DSC08697", "DSC08729", "DSC08735", "DSC08736", "IMG_6739", "IMG_6743", "P1010013-3", "P1010017-3",
]);
/** Whether the drive's photo `folder`/`name` may appear on the page: any of
 * its own folders', and of the private photoshoot only PRIVATE_ALLOWED's. */
export const driveAllowed = ({ folder, name }) => folder !== PRIVATE_FOLDER || PRIVATE_ALLOWED.some((id) => name.endsWith(`-${id}.jpg`));

/** The photos on white (the cover's): the product folder's on a white
 * backdrop (their border's brightness measured, the cleanest kept), the
 * first the one on top (and the phone's one picture). The photo option
 * (js/options.js lists the ids again; tests/redesign.test.mjs keeps the two
 * in step) puts another of them on top. */
export const WHITE_PHOTOS = Object.freeze(["DSC04215", "DSC04219", "DSC04223", "DSC04229", "DSC04205", "DSC04225"]);

export const SECTIONS = deepFreeze([
  // 3-4. Over the cross once it is upright (1-2 are the hero's opening):
  // first the product's text, plain (no window); then it goes and the
  // photos on white open one after another as a stack of windows, the
  // first on top (sections/cover.js). Part of the hero (it follows the
  // hero's scroll), so in the theme: settings and blocks of the hero's
  // section.
  //
  // The text: the live page's first section's title and tagline, verbatim;
  // the paragraph is Kyle's own sentence, not the store's.
  {
    type: "nc-cover",
    settings: {
      title: "Nothing Radio Cross USB",
      tagline: "Consider yourself blessed.",
      lines: [
        "Inspired by Justice, this transparent resin USB has been years in the making.",
      ],
      // one quiet line under it: the drive's storage, its plug and its light
      // (Kyle's words, "64gb, illuminates red"; it is USB 3.0, checked on
      // the piece); empty: none
      specs: "64GB · USB 3.0 · Illuminates red",
      // beside the photos on white once they open (left of the stack on a
      // computer, under the photo on a phone): the store owner's why
      // paragraph, verbatim from his page (its no-break space too); empty:
      // none, the stack centred
      about: [
        "Designed by Carter Houck and Kyle Chang, the Cross USB started as a\u00a0fun project aimed to make a creative usb for DJ's. Every USB we saw when going out was pure utility. Obviously nothing wrong with that but with the cultural worship of DJ's and the DJ's being front and center why not adorn your hardware.",
      ],
      // the buy bubble's note, beside the price (sections/bar.js); empty:
      // none. A cover setting, as the bubble comes in with the product's
      // text and the theme's section has the cover's settings together.
      barNote: "Limited to 10 pieces.",
    },
    blocks: WHITE_PHOTOS.map(product),
  },
  // The live page's sections after its first, in its order, each in this
  // page's design. Its text plain, in the page's type; every photo and video
  // a window, but for the scanning video, full screen.
  //
  // 5. The scanning video (a scanner's light sweeping across the cross in
  // the dark), full screen with its paragraph over it (a heading over that
  // only if the page has others: Kyle's "Crystal clear" waits, empty, as
  // nothing else on the page has one; the setting is there to add it):
  // the page's one break from the windows (sections/fullscreen.js). Its
  // paragraph is Kyle's sentence (2026-10-21), in place of the store's.
  {
    type: "nc-fullscreen",
    settings: {
      heading: "",
      lines: [
        "Made from clear resin, the USB's glass-like ridges bend light through every facet.",
      ],
    },
    blocks: [
      { type: "video", ...clip("6c9938346c824956b310e31de9cfb8fa", "HD-1080p-7.2Mbps-97252390", 1920), ratio: WIDE },
    ],
  },
  // 6. The featured video, AliRQ at Le Bain: one video window, its caption
  // in its bar (sections/media.js); or, by default, the gallery's first
  // video instead (withFeaturedVideo, the featuredvideo option). (The live page's "why" paragraph beside it is
  // parked off the page until Kyle comes back to it; its "tested" beat is
  // cut.)
  {
    type: "nc-media",
    settings: { name: "alirq" },
    blocks: [
      { type: "video", ...clip("6fab8bdcc8a54ea5848d28bbce1ea476", "HD-1080p-7.2Mbps-97197629"), ratio: WIDE, caption: "AliRQ with Cross USB at Le Bain." },
    ],
  },
  // 7. The photoshoot's stack: a stack of windows, the red photo on top
  // (the live page's photo after its grid, IMG_6743: the drive's copy is the
  // same picture), then the two of the cross's light, then the rest in
  // Kyle's order (sections/media.js)
  {
    type: "nc-media",
    settings: { name: "stack" },
    blocks: ["IMG_6743", "P1010013-3", "P1010017-3", ...PRIVATE_ALLOWED.filter((id) => !["IMG_6743", "P1010013-3", "P1010017-3"].includes(id))].map(shoot),
  },
  // 8. The gallery: the live page's strip of photos (its grid, after its
  // specs), then its five party clips (that strip's videos), then the
  // drive's scanner photos, every one a window on a pile with the drive's
  // file browser behind it (sections/gallery.js)
  {
    type: "nc-gallery",
    settings: {},
    blocks: [
      photo("IMG_9483_Large_888b8009-5ba4-423b-a831-ac7ccd3c3d1b.jpg", 1600, 2133, "IMG_9483.jpg"),
      photo("IMG_3537_Large_dc12e1b6-d69c-4f7d-b1ae-3171746681ee.jpg", 1600, 2133, "IMG_3537.jpg"),
      photo("IMG_8189_Large_44feeacd-5771-4b26-82ad-4ddc00237b39.jpg", 1600, 2837, "IMG_8189.jpg"),
      photo("Screenshot_2026-10-12_at_4.33.34_PM.png", 1600, 1919, "Screenshot_2026-10-12_at_4.33.34_PM.png"),
      { type: "video", ...clip("ff1d0e2c4e154169aa5c8cc34817e1f5", "HD-1080p-7.2Mbps-97229160"), ratio: UPRIGHT, caption: null },
      { type: "video", ...clip("47b76e27b9264afc9184c3120a41cfb9", "HD-1080p-7.2Mbps-97232891"), ratio: UPRIGHT, caption: null },
      { type: "video", ...clip("73cd50c57fec44d08cecad45b4f7afaa", "HD-1080p-7.2Mbps-97233002"), ratio: UPRIGHT, caption: null },
      { type: "video", ...clip("c841bdc21a604cf19ad270c9f5b8e0c5", "HD-1080p-7.2Mbps-97233067"), ratio: UPRIGHT, caption: null },
      { type: "video", ...clip("46c8cca4693648c1994aabcb27f2e1ba", "HD-1080p-7.2Mbps-97233173"), ratio: UPRIGHT, caption: null },
      ...["DSC04280", "DSC04281", "DSC04282", "DSC04283", "DSC04284", "DSC04285", "DSC04286", "DSC04287", "DSC04288", "DSC04291", "DSC04292", "DSC04293", "DSC04294", "DSC04295", "DSC04296", "DSC04297", "DSC04298"].map(scanner),
    ],
  },
  // 9. The file browser: the drive's own window (js/drive/), behind the
  // gallery's pile, or (gallery=grid) its own section after it
  {
    type: "nc-drive",
    settings: {},
    blocks: [],
  },
]);

// (The buy bubble, the page's only add to cart, has one content of its own,
// the cover's barNote; the rest is the page's buy control, sections/bar.js.)

/** `sections` with every drive photo block ({ type: "photo", folder, name })
 * as the drive's photo, { type: "photo", item } (its manifest item). Throws
 * on one the drive doesn't have, and on one the page may not show
 * (driveAllowed: the private photoshoot's but for those allowed). The
 * page's (redesign.js), once the drive's manifest is read. */
export function resolveDrivePhotos(sections, manifest) {
  return sections.map((section) => ({
    ...section,
    blocks: section.blocks.map((block) => {
      if (block.type !== "photo" || block.src) return block;
      const where = `${block.folder}/${block.name}`;
      if (!driveAllowed(block)) throw new Error(`redesign: the drive's photo "${where}" may not be shown on the page (content.js's driveAllowed)`);
      if (!manifest) throw new Error(`redesign: the photo "${where}" is the drive's, and the drive has no photos folder`);
      const item = manifest.items.find((it) => it.folder === block.folder && it.name === block.name);
      if (!item) throw new Error(`redesign: the photo "${where}" isn't on the drive (content.js)`);
      return { type: "photo", item };
    }),
  }));
}

/** Where the featured video (the AliRQ clip, the nc-media "alirq"
 * section's one video) stands (the featuredvideo option; the section's
 * "Featured video placement"): "gallery", an ordinary window of the
 * gallery, first of its videos (before the live page's party clips); or
 * "section", a section of its own after the scanning video. (js/options.js
 * lists them again; tests/redesign.test.mjs keeps the two in step.) */
export const FEATURED_PLACES = Object.freeze(["gallery", "section"]);

/** `sections` with the featured video where `place` (FEATURED_PLACES) puts
 * it: as they are for "section"; for "gallery", its section gone and its
 * video the gallery's, before the gallery's first video (or last, if it
 * has none). Shown once either way. */
export function withFeaturedVideo(sections, place) {
  if (!FEATURED_PLACES.includes(place)) throw new Error(`redesign: no featured video placement "${place}"; expected one of ${FEATURED_PLACES.join(", ")}`);
  if (place === "section") return sections;
  const featured = sections.find((s) => s.type === "nc-media" && s.settings.name === "alirq");
  if (!featured) throw new Error("redesign: the content has no featured video (nc-media alirq)");
  return deepFreeze(sections.filter((s) => s !== featured).map((s) => {
    if (s.type !== "nc-gallery") return s;
    const at = s.blocks.findIndex((b) => b.type === "video");
    const blocks = [...s.blocks];
    blocks.splice(at < 0 ? blocks.length : at, 0, ...featured.blocks);
    return { ...s, blocks };
  }));
}

/** `sections` with the photo on white whose id is `top` on top (first),
 * the others in their order: the photo option's (js/options.js). */
export function withWhiteOnTop(sections, top) {
  const cover = sections.find((s) => s.type === "nc-cover");
  const i = cover.blocks.findIndex((b) => b.name?.endsWith(`-${top}.jpg`));
  if (i < 0) throw new Error(`redesign: no photo on white ${top} among the cover's photos`);
  const blocks = [cover.blocks[i], ...cover.blocks.filter((_, j) => j !== i)];
  return deepFreeze(sections.map((s) => (s === cover ? { ...s, blocks } : s)));
}

/** The theme's section's content (shopify/sections/nothing-cross.liquid,
 * #nc-config's redesign: its settings, and its blocks in the editor's order,
 * each flat, { type, ...its values }) in SECTIONS' shape. The section's
 * settings and block types are this file's, one for one
 * (scripts/shopify-package.mjs, REDESIGN_SETTINGS and REDESIGN_BLOCKS);
 * a text area's lines are its non-empty lines. */
export function fromSection({ settings: s, blocks }) {
  const known = ["cover_photo", "scan_video", "end_video", "alirq_video", "gallery_photo", "gallery_video"];
  const unknown = blocks.find((b) => !known.includes(b.type));
  if (unknown) throw new Error(`redesign: the section has a block of type "${unknown.type}", which the page doesn't know`);
  // the blocks of `types`, in the editor's order, each as content.js's
  // type of it (photo or video)
  const of = (types) => blocks.filter((b) => types.includes(b.type)).map(({ type, ...values }) => ({ type: type.endsWith("_video") ? "video" : "photo", ...values }));
  // a text area of the drive's photos, as photo blocks
  const drivePhotos = (text) => drivePhotoLines(text).map((p) => ({ type: "photo", ...p }));
  return deepFreeze([
    {
      type: "nc-cover",
      settings: { title: s.cover_title, price: (s.cover_price ?? "").trim(), tagline: s.cover_tagline, lines: lines(s.cover_lines), specs: (s.cover_specs ?? "").trim(), about: lines(s.cover_about), barNote: (s.cover_bar_note ?? "").trim() },
      blocks: of(["cover_photo"]),
    },
    { type: "nc-fullscreen", settings: { heading: (s.scan_heading ?? "").trim(), lines: lines(s.scan_lines) }, blocks: of(["scan_video"]) },
    { type: "nc-media", settings: { name: "alirq", caption: "adorn your hardware" }, blocks: of(["alirq_video"]) },
    { type: "nc-media", settings: { name: "stack", caption: "adorn your hardware" }, blocks: drivePhotos(s.stack_photos) },
    { type: "nc-gallery", settings: {}, blocks: [...of(["gallery_photo", "gallery_video"]), ...drivePhotos(s.gallery_drive_photos)] },
    { type: "nc-drive", settings: {}, blocks: [] },
    // a phone's last video, full screen, no text (hidden on a wide screen: css/redesign/fullscreen.css)
    ...(blocks.some((b) => b.type === "end_video") ? [{ type: "nc-fullscreen", settings: { heading: "", lines: [], name: "end" }, blocks: of(["end_video"]) }] : []),
  ]);
}

// a text area's non-empty lines, trimmed
const lines = (text) => (text ?? "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

/** The drive's photos a text area names, one a line, each as its folder
 * and file name, "folder/name" (the section's stack and gallery photos), in
 * order: [{ folder, name }]. A line without a "/" has no folder (""): the
 * page, and the editor's check, say it isn't on the drive. */
export const drivePhotoLines = (text) => lines(text).map((line) => {
  const i = line.indexOf("/");
  return i < 0 ? { folder: "", name: line } : { folder: line.slice(0, i).trim(), name: line.slice(i + 1).trim() };
});

/** drivePhotoLines' inverse: the text area's lines for `photos`. */
export const drivePhotoText = (photos) => photos.map((p) => `${p.folder}/${p.name}`).join("\n");

function deepFreeze(value) {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const v of Object.values(value)) deepFreeze(v);
  }
  return value;
}
